import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Edit } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const RenameFileModal = ({ isOpen, onClose, onFileRenamed, file }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (file) {
      setName(file.subject || file.name || '');
    }
  }, [file]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      let url = `/api/efile/${file.id}/subject`;
      let payload = { subject: name };

      if (file.isFolder) {
        url = `/api/efile/folder/${file.id}`;
        payload = { name: name };
      }

      const response = await axios.put(
        url,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Renamed successfully');
        // If it was a folder rename, the response might return the updated folder object.
        // We should ensure the parent component updates the list correctly.
        // The backend updateFolder returns { ...folder, name: name.trim() }
        // The backend updateSubject returns "Subject updated" message but maybe not the object?
        // Let's check updateSubject return.
        
        // updateSubject returns: sendResponse(res, 200, true, 'Subject updated');
        // It does NOT return the data.
        // So we need to construct the updated file object manually or refetch.
        
        const updatedFile = { ...file, subject: name, name: name }; // handle both properties
        onFileRenamed(updatedFile);
        onClose();
      }
    } catch (error) {
      console.error('Error renaming file:', error);
      toast.error(error.response?.data?.message || 'Failed to rename');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Rename ${file?.isFolder ? 'Folder' : 'File'}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            New Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter new name..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full"
            required
            autoFocus
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="flex items-center gap-2">
            <Edit className="h-4 w-4" />
            {loading ? 'Renaming...' : 'Rename'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RenameFileModal;
